import {
  ApolloClient,
  HttpLink,
  InMemoryCache,
  Operation,
} from "@apollo/client";
import { ApolloLink, from, split } from "@apollo/client/link/core";
import { onError } from "@apollo/client/link/error";
import {
  getMainDefinition,
  relayStylePagination,
} from "@apollo/client/utilities";
import { setContext } from "@apollo/link-context";
import * as ActionCable from "@rails/actioncable";
import { CachePersistor, LocalStorageWrapper } from "apollo3-cache-persist";
import ActionCableLink from "graphql-ruby-client/subscriptions/ActionCableLink";
import Cookies from "js-cookie";

import fragmentMatcher from "./gql/fragmentMatcher";

const inMemoryCache = new InMemoryCache({
  possibleTypes: fragmentMatcher.possibleTypes,
  typePolicies: {
    Subscription: {
      fields: {
        messageSent: {
          // Prevents the error "Missing field `messageSent` while writing result {}"
          // which appears when subscribing for the first time. Found this hack by
          // reading source code https://github.com/apollographql/apollo-client/blob/v3.7.0/src/cache/inmemory/writeToStore.ts#L355
          read: (existing) => existing,
        },
        messageRead: {
          // Prevents the error "Missing field `messageRead` while writing result {}"
          // which appears when subscribing for the first time. Found this hack by
          // reading source code https://github.com/apollographql/apollo-client/blob/v3.7.0/src/cache/inmemory/writeToStore.ts#L355
          read: (existing) => existing,
        },
      },
    },

    Chat: {
      fields: {
        messages: relayStylePagination(["channel"]),
      },
    },
  },
});

const getCableUrl = () => {
  const apiUrl = import.meta.env.VITE_API_URL?.toString() ?? "";
  const [protocol, baseUrl] = apiUrl.split("//");
  const wsProtocol = protocol === "https:" ? "wss:" : "ws:";
  const token = Cookies.get("token");
  return `${wsProtocol}//${baseUrl}/cable?token=${token}`;
};

const createActionCableLink = () => {
  const cable = ActionCable.createConsumer(getCableUrl());
  Object.defineProperty(cable, "url", {
    get: function () {
      return getCableUrl();
    },
  });
  return new ActionCableLink({ cable });
};

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors?.[0]?.extensions?.code === "UNAUTHENTICATED") {
    window.location.replace("/session-timeout");
  }
});

const httpLink = new HttpLink({
  uri: `${import.meta.env.VITE_API_URL}/api/graphql`,
});

const hasSubscriptionOperation = ({ query }: Operation) => {
  const definition = getMainDefinition(query);
  return (
    definition.kind === "OperationDefinition" &&
    definition.operation === "subscription"
  );
};

const authLink = setContext((_, { headers }) => {
  const token = Cookies.get("token");
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const refreshTokenLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const context = operation.getContext();
    const authorization = context.response?.headers.get("Authorization") ?? "";
    if (authorization) {
      const [, token] = authorization.split(" ");
      Cookies.set("token", token, { secure: true });
    }
    return response;
  });
});

const client = new ApolloClient({
  link: from([
    errorLink,
    authLink,
    refreshTokenLink,
    split(hasSubscriptionOperation, createActionCableLink(), httpLink),
  ]),
  cache: inMemoryCache,
  defaultOptions: {
    query: {
      fetchPolicy: "network-only",
    },
    watchQuery: {
      fetchPolicy: "cache-and-network",
    },
  },
});

export const persistor = new CachePersistor({
  cache: inMemoryCache,
  storage: new LocalStorageWrapper(window.localStorage),
  debug: import.meta.env.MODE === "development",
});

export default client;
